/**
 * live/set-flow.ts — l'andamento dei set ricostruito dal live.
 *
 * Riempie la **stessa forma** che il match center usa per il referto (`SetFlow`
 * di `lib/match-analysis`), così la pagina pubblica del live può disegnare gli
 * stessi grafici (`SetFlowChart` / `SetFlowChartBars`) senza un secondo
 * componente da tenere allineato.
 *
 * Differenza sostanziale rispetto al referto — e vale la pena sfruttarla: dal
 * referto conosciamo solo i punteggi ai **cambi di servizio**, e `flowGeometry`
 * ricostruisce gli scambi intermedi applicando la regola del rally point. Qui
 * invece abbiamo **ogni punto con chi l'ha fatto** (`live_match_events`:
 * `type='point'`, `team`, `payload.previousState`). Quindi ogni punto diventa un
 * turno e la sequenza disegnata è quella reale, non una ricostruzione.
 *
 * Nel rally point chi vince lo scambio va a servire: `side` del turno è chi ha
 * fatto il punto, che è anche chi riprende la battuta. Il numero di maglia del
 * battitore il live non lo conosce (l'operatore segna il punto, non il
 * battitore): resta `null` e il grafico mostra il pallino senza nome.
 */
import type { SetFlow, SetFlowSide, SetFlowTurn } from '@/lib/match-analysis'
import { SCORING_EVENT_TYPES } from './undo'

/** L'evento come lo serve `/api/live/[sessionKey]/events`. */
export interface LiveFlowEvent {
  id: number
  revision: number
  type: string
  team: string | null
  payload: Record<string, unknown>
  created_at: string
}

interface PrevState {
  currentSet?: number
  currentScore?: { home?: number; away?: number }
}

/** Un punto del live: chi l'ha fatto e com'era il punteggio **dopo**. */
interface LivePoint {
  set: number
  side: 'home' | 'away'
  home: number
  away: number
}

function pointFrom(e: LiveFlowEvent): LivePoint | null {
  // Un punto è punto in tutte le sue forme: `point` è il tocco generico, ma
  // `ace`, `attack`, `block`, `defense` e `opponent_error` valgono uguale. Prima
  // passava solo `point`, quindi un set chiuso con un ace spariva dal grafico e
  // risultava `verified: false` (l'ultimo turno non tornava col parziale).
  if (!SCORING_EVENT_TYPES.has(e.type)) return null
  if (e.team !== 'home' && e.team !== 'away') return null
  const prev = e.payload?.previousState as PrevState | undefined
  const before = prev?.currentScore
  const set = Number(prev?.currentSet ?? 1)
  if (!before || typeof before.home !== 'number' || typeof before.away !== 'number') return null
  const home = before.home + (e.team === 'home' ? 1 : 0)
  const away = before.away + (e.team === 'away' ? 1 : 0)
  return { set, side: e.team, home, away }
}

/** I punteggi a cui la squadra **riprende** a battere: la sua serie crescente. */
function changesFor(turns: SetFlowTurn[], side: 'home' | 'away'): number[] {
  return turns
    .filter((t) => t.side === side)
    .map((t) => (side === 'home' ? t.home : t.away))
    .filter((v, i, all) => i === 0 || v > all[i - 1])
}

function side(changes: number[]): SetFlowSide {
  // `starters`/`libero`/`timeouts` non sono disponibili dal live: il grafico e le
  // schede laterali del match center li usano solo quando ci sono.
  return { starters: [], changes }
}

/**
 * Costruisce un `SetFlow` per ogni set per cui esistono punti.
 *
 * `setHistory` (dallo stato del live) serve solo a **verificare** la
 * ricostruzione: se l'ultimo turno di un set chiuso non coincide col parziale
 * registrato, il set è marcato `verified: false` invece di essere disegnato come
 * se fosse coerente.
 */
export function buildLiveSetFlows(
  events: LiveFlowEvent[],
  setHistory: Array<{ home: number; away: number }> = [],
): SetFlow[] {
  const points = events
    .map(pointFrom)
    .filter((p): p is LivePoint => p !== null)
    .sort((a, b) => a.set - b.set || a.home + a.away - (b.home + b.away) || a.home - b.home)

  if (points.length === 0) return []

  const bySet = new Map<number, LivePoint[]>()
  for (const p of points) {
    const list = bySet.get(p.set)
    if (list) list.push(p)
    else bySet.set(p.set, [p])
  }

  return [...bySet.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([set, list]) => {
      const turns: SetFlowTurn[] = list.map((p) => ({
        side: p.side,
        number: null,
        player: null,
        home: p.home,
        away: p.away,
      }))
      const last = list[list.length - 1]
      const expected = setHistory[set - 1]
      return {
        set,
        // Il live non registra chi è partito in battuta: si sa solo chi ha vinto
        // il primo scambio, che è chi va a servire **dopo**.
        startsServing: null,
        home: side(changesFor(turns, 'home')),
        away: side(changesFor(turns, 'away')),
        turns,
        verified: expected ? expected.home === last.home && expected.away === last.away : false,
      }
    })
}

/** I segni sul grafico (time-out) ricavati dagli eventi, con lo scambio esatto. */
export function buildLiveMarkers(
  events: LiveFlowEvent[],
): Array<{ set: number; total: number; side: 'home' | 'away'; label: string; title: string; labelSide: 'top' }> {
  const out: Array<{ set: number; total: number; side: 'home' | 'away'; label: string; title: string; labelSide: 'top' }> = []
  for (const e of events) {
    if (e.type !== 'timeout' && e.type !== 'timeout_home' && e.type !== 'timeout_away') continue
    const prev = e.payload?.previousState as PrevState | undefined
    const s = prev?.currentScore
    if (!s || typeof s.home !== 'number' || typeof s.away !== 'number') continue
    const sideOf: 'home' | 'away' | null =
      e.team === 'home' || e.team === 'away'
        ? e.team
        : e.type === 'timeout_home'
          ? 'home'
          : e.type === 'timeout_away'
            ? 'away'
            : null
    if (!sideOf) continue
    out.push({
      set: Number(prev?.currentSet ?? 1),
      total: s.home + s.away,
      side: sideOf,
      label: 'T',
      title: `Time-out ${sideOf === 'home' ? 'casa' : 'ospiti'} sul ${s.home}-${s.away}`,
      labelSide: 'top',
    })
  }
  return out
}
